import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { UIIconBtn, UITabBlock } from '../components/basic';
import { UISelectMapArea, UISelectSiteOrder } from '../components/composite';
import { PopupMenuAlert } from '../components/popups';
import { useNavigation } from '../../_NavigationContext';
import { useUserPreferences } from '../../_UserPreferencesContext';
import { useMapContext } from '../../_MapContext';
import { useLang } from '../../_LangContext';
import MAP_DATA from '../../../_json/_map_data.json';
import ARService from '../../services/ARService';
import GTM from '../../_GTM';
import NotificationCookies from '../../_NotificationCookies';
import ProximityManager from '../shared/ProximityManager';

const MenuScreenExhibits = ({ onPinClick, onInfoClick, onARClick }) =>
{
    const location = useLocation();
    const { lang, getText, getMessage, getItemData } = useLang();
    const { navigateSetParam } = useNavigation();
    const { siteFilter, siteOrder, gpsCheckEnabled, updatePreferences } = useUserPreferences();
    const { mapInstance } = useMapContext();
    const [arEnabledItems, setArEnabledItems] = useState({});
    const [alertModalOpen, setAlertModalOpen] = useState(false);
    const [alertModalData, setAlertModalData] = useState(null);
    const proximityManager = new ProximityManager();

    const handleSelectMapAreaChange = useCallback((item) =>
    {
        if (!item?.value) return;
        const newValue = item.value;
        updatePreferences({ siteFilter: newValue, siteOrder });
        navigateSetParam("filter", newValue, 'all');
    },
        [updatePreferences, siteOrder, navigateSetParam]);

    const handleSelectSiteOrderChange = useCallback((item) =>
    {
        if (!item?.value) return;
        const newValue = item.value;
        updatePreferences({ siteFilter, siteOrder: newValue });
        navigateSetParam("sort", newValue, 'default');
    },
        [updatePreferences, siteFilter, navigateSetParam]);

    // AR proximity checking setup
    useEffect(() =>
    {
        // Get buildings that have AR and add AR property for ProximityManager
        const arBuildings = ARService.filterARBuildings(MAP_DATA.buildings || []);
        const arBuildingsWithProperty = ARService.addARPropertyToBuildings(arBuildings);

        if (!arBuildingsWithProperty.length) return;

        const cleanup = proximityManager.setupProximityUpdates(
            arBuildingsWithProperty,
            gpsCheckEnabled,
            setArEnabledItems
        );

        return cleanup || (() => { });
    },
        [gpsCheckEnabled]);

    const filterSiteItems = (item) =>
    {
        if (siteFilter === 'all')
        {
            return true;
        }
        else if (siteFilter === 'ar')
        {
            // Use ARService to check if building has AR
            return ARService.hasAR(item.id);
        }
        else
        {
            return item.zone === siteFilter;
        }
    };

    const sortSiteItems = (a, b) =>
    {
        if (siteOrder === 'year')
        {
            return a.year_sort - b.year_sort;
        }
        else if (siteOrder === 'zone')
        {
            if (a.zone !== b.zone) return a.zone.localeCompare(b.zone);
            return a.zone_site_id - b.zone_site_id;
        }
        return a.id - b.id;
    };

    const getLocalizedItemData = (structuralItem, type) =>
    {
        return getItemData(type, structuralItem.id);
    };

    const handleIconPinClick = useCallback((item_type, item) =>
    {
        if (onPinClick)
        {
            onPinClick(item_type, item);
        }
    },
        [onPinClick]);

    const handleARClick = useCallback((item_type, item) =>
    {
        if (onARClick)
        {
            onARClick(item_type, item);
        }
    },
        [onARClick]);

    // URL parameter handling
    useEffect(() =>
    {
        const params = new URLSearchParams(window.location.search);
        const filterFromUrl = params.get('filter');
        const orderFromUrl = params.get('sort');

        // Only update if URL params exist and are different from current state
        if (filterFromUrl && filterFromUrl !== siteFilter) 
        {
            updatePreferences({ siteFilter: filterFromUrl, siteOrder });
        }

        if (orderFromUrl && orderFromUrl !== siteOrder) 
        {
            updatePreferences({ siteFilter, siteOrder: orderFromUrl });
        }
    },
        [location.search, siteFilter, siteOrder, updatePreferences]);

    const tabs =
        [
            {
                key: 'buildings',
                label: getText('type1'),
                className: "sites",
                content: (
                    <>
                        <div className="block filters">
                            <UISelectMapArea value={siteFilter} onChange={handleSelectMapAreaChange} />
                            <UISelectSiteOrder value={siteOrder} onChange={handleSelectSiteOrderChange} />
                        </div>
                        <div className="site-items">
                            {MAP_DATA.buildings
                                ?.filter(filterSiteItems)
                                .sort(sortSiteItems)
                                .map((item) =>
                                {
                                    const img = `${process.env.PUBLIC_URL}/img/tatemono/tatemono_thumb_${item.zone}_${("00" + item.zone_site_id).slice(-2)}.jpg`;
                                    const localizedItem = getLocalizedItemData(item, 'buildings');

                                    return (
                                        <div key={item.id} className="site-item site" data-id={item.id}>
                                            <figure
                                                className="thumb"
                                                onClick={() => onInfoClick('site', item)}
                                                style={{ backgroundImage: 'url(' + img + ')' }}
                                            />
                                            <div className="item-name" onClick={() => onInfoClick('site', item)}>
                                                <div className="name">{localizedItem?.name || item.name || ''}</div>
                                            </div>
                                            <div className="item-links">
                                                {ARService.hasAR(item.id) && (
                                                    <div
                                                        className={`tag-item ar ${arEnabledItems[item.id] !== true ? 'disabled' : ''}`}
                                                        onClick={() => handleARClick('site', item)}
                                                    >
                                                        <p>AR</p>
                                                    </div>
                                                )}
                                                <UIIconBtn
                                                    className="raw btn-pin"
                                                    btnKey={`site-${item.id}`}
                                                    iconClass="map-pin"
                                                    onClick={() => handleIconPinClick('site', item)}
                                                />
                                            </div>
                                        </div>
                                    );
                                }
                                )}
                        </div>
                    </>
                )
            },
            {
                key: 'exteriors',
                label: getText('type2'),
                className: "exterior",
                content: (
                    <div className="site-items">
                        {MAP_DATA.exteriors?.map((item) =>
                        {
                            const localizedItem = getLocalizedItemData(item, 'exteriors');
                            const img = `${process.env.PUBLIC_URL}/img/exterior/exterior_thumb_${("00" + item.id).slice(-2)}_1.jpg`;

                            return (
                                <div key={item.id} className="site-item exterior" data-id={item.id}>
                                    <figure
                                        className="thumb"
                                        onClick={() => onInfoClick('exterior', item)}
                                        style={{ backgroundImage: 'url(' + img + ')' }}
                                    />
                                    <div className="item-name" onClick={() => onInfoClick('exterior', item)}>
                                        <div className="name">{localizedItem?.name || item.name || ''}</div>
                                    </div>
                                    <div className="item-links">
                                        <UIIconBtn
                                            className="raw btn-pin"
                                            btnKey={`exterior-${item.id}`}
                                            iconClass="map-pin"
                                            onClick={() => handleIconPinClick('exterior', item)}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )
            },
            {
                key: 'restaurants',
                label: getText('restaurants'),
                className: "foods",
                content: (
                    <div className="site-items">
                        {MAP_DATA.restaurants?.map((item) =>
                        {
                            const img = `${process.env.PUBLIC_URL}/img/restaurant/restaurant_thumb_${("00" + item.id).slice(-2)}.jpg`;
                            const localizedItem = getLocalizedItemData(item, 'restaurants');

                            return (
                                <div key={item.id} className="site-item food" data-id={item.id}>
                                    <figure
                                        className="thumb"
                                        onClick={() => onInfoClick('food', item)}
                                        style={{ backgroundImage: 'url(' + img + ')' }}
                                    />
                                    <div className="item-name" onClick={() => onInfoClick('food', item)}>
                                        <div className="name">{localizedItem?.name || item.name || ''}</div>
                                    </div>
                                    <div className="item-links">
                                        <UIIconBtn
                                            className="raw btn-pin"
                                            btnKey={`site-${item.id}`}
                                            iconClass="map-pin"
                                            onClick={() => handleIconPinClick('food', item)}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )
            }
        ];

    const handleTabChange = useCallback((index, label) =>
    {
        if (label === getText('type2'))
        {
            const shouldShow = NotificationCookies.shouldShowAlert('exteriorTabOpened');
            if (shouldShow)
            {
                setAlertModalData(getMessage('alert-exterior-pins'));
                setAlertModalOpen(true);
                NotificationCookies.markAlertShown('exteriorTabOpened');
            }
        }

        GTM.sendEvent('tab_change',
            {
                content_group: 'map',
                lang: lang,
                page_name: getText('exhibits'),
                index: index,
                name: label,
            });
    },
        [lang, getText]);

    return (
        <>
            <div className="modal-screen exhibits" data-id="1">
                <UITabBlock
                    componentID="menu-screen-exhibits"
                    preserveTabState={true}
                    tabs={tabs}
                    onTabChange={handleTabChange}
                />
            </div>
            <PopupMenuAlert
                isOpen={alertModalOpen}
                onClose={() => setAlertModalOpen(false)}
                messageData={alertModalData}
            />
        </>
    );
};

export default MenuScreenExhibits; 